/**
 * features/products/hooks.ts
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProductInput } from '@ft/core';
import { api, queryString } from '~/lib/api';
import { queryKeys } from '~/lib/query-client';
import type { Paginated, Product } from '~/lib/types';

export interface ProductListParams {
  page: number;
  pageSize: number;
  search: string;
}

export function useProducts(params: ProductListParams) {
  return useQuery({
    queryKey: queryKeys.products(params),
    queryFn: () =>
      api.get<Paginated<Product>>(
        `/api/products${queryString({ page: params.page, pageSize: params.pageSize, search: params.search })}`,
      ),
    placeholderData: (previous) => previous,
  });
}

export function useCreateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ProductInput) => api.post<Product>('/api/products', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ProductInput }) =>
      api.put<Product>(`/api/products/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useDeleteProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/products/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}
